import React, { useState, useRef, useEffect } from 'react';
import './TimePicker.css';

const TimePicker = ({ value, onChange, name, id, className = '', error, required, placeholder = 'Select time' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAbove, setShowAbove] = useState(false);
  const [time, setTime] = useState(value || { hour: 9, minute: 0, period: 'AM' });
  const [timeInputValue, setTimeInputValue] = useState('');
  const pickerRef = useRef(null);
  const popupRef = useRef(null);

  // Update internal time when value prop changes
  useEffect(() => {
    if (value) {
      const parsed = parseTimeValue(value);
      if (parsed) {
        setTime(parsed);
        // Update input value to match
        setTimeInputValue(`${String(parsed.hour).padStart(2, '0')}:${String(parsed.minute).padStart(2, '0')}`);
      }
    } else {
      setTimeInputValue('');
    }
  }, [value]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const parseTimeValue = (timeStr) => {
    if (!timeStr) return null;
    
    // Handle HH:MM format
    const match = timeStr.match(/(\d{1,2}):(\d{2})/);
    if (match) {
      let hour = parseInt(match[1], 10);
      const minute = parseInt(match[2], 10);
      const period = hour >= 12 ? 'PM' : 'AM';
      
      if (hour === 0) hour = 12;
      else if (hour > 12) hour = hour - 12;
      
      return { hour, minute, period };
    }
    return null;
  };

  const formatTimeValue = (timeObj) => {
    let hour24 = timeObj.hour;
    if (timeObj.period === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (timeObj.period === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    
    const hourStr = String(hour24).padStart(2, '0');
    const minuteStr = String(timeObj.minute).padStart(2, '0');
    return `${hourStr}:${minuteStr}`;
  };

  const formatDisplayTime = (timeObj) => {
    const hourStr = String(timeObj.hour).padStart(2, '0');
    const minuteStr = String(timeObj.minute).padStart(2, '0');
    return `${hourStr}:${minuteStr} ${timeObj.period}`;
  };

  const handleTimeChange = (newTime) => {
    setTime(newTime);
    const timeValue = formatTimeValue(newTime);
    if (onChange) {
      onChange({
        target: {
          name: name,
          value: timeValue
        }
      });
    }
  };

  const incrementHour = () => {
    let newHour = time.hour + 1;
    if (newHour > 12) {
      newHour = 1;
    }
    handleTimeChange({ ...time, hour: newHour });
  };

  const decrementHour = () => {
    let newHour = time.hour - 1;
    if (newHour < 1) {
      newHour = 12;
    }
    handleTimeChange({ ...time, hour: newHour });
  };

  const incrementMinute = () => {
    let newMinute = time.minute + 1;
    if (newMinute >= 60) {
      newMinute = 0;
      incrementHour();
      return;
    }
    handleTimeChange({ ...time, minute: newMinute });
  };

  const decrementMinute = () => {
    let newMinute = time.minute - 1;
    if (newMinute < 0) {
      newMinute = 59;
      decrementHour();
      return;
    }
    handleTimeChange({ ...time, minute: newMinute });
  };

  const handleHourInputChange = (e) => {
    const value = e.target.value;
    // Allow empty or numeric input
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = value === '' ? null : parseInt(value, 10);
      if (numValue === null) {
        // Allow empty temporarily while typing
        setTime(prev => ({ ...prev, hour: '' }));
      } else if (numValue >= 1 && numValue <= 12) {
        handleTimeChange({ ...time, hour: numValue });
      }
    }
  };

  const handleHourBlur = (e) => {
    const value = e.target.value;
    const numValue = parseInt(value, 10);
    if (value === '' || isNaN(numValue) || numValue < 1 || numValue > 12) {
      // Reset to valid value
      const currentHour = typeof time.hour === 'number' ? time.hour : 1;
      handleTimeChange({ ...time, hour: currentHour });
    }
  };

  const handleMinuteInputChange = (e) => {
    const value = e.target.value;
    // Allow empty or numeric input
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = value === '' ? null : parseInt(value, 10);
      if (numValue === null) {
        // Allow empty temporarily while typing
        setTime(prev => ({ ...prev, minute: '' }));
      } else if (numValue >= 0 && numValue <= 59) {
        handleTimeChange({ ...time, minute: numValue });
      }
    }
  };

  const handleMinuteBlur = (e) => {
    const value = e.target.value;
    const numValue = parseInt(value, 10);
    if (value === '' || isNaN(numValue) || numValue < 0 || numValue > 59) {
      // Reset to valid value
      const currentMinute = typeof time.minute === 'number' ? time.minute : 0;
      handleTimeChange({ ...time, minute: currentMinute });
    }
  };

  const handleTimeDisplayChange = (e) => {
    let input = e.target.value;
    
    // Remove any non-digit and colon characters
    input = input.replace(/[^\d:]/g, '');
    
    // Auto-format: add colon after 2 digits if not present
    if (input.length === 2 && !input.includes(':')) {
      input = input + ':';
    }
    
    // Limit to HH:MM format (max 5 chars: HH:MM)
    if (input.length > 5) {
      input = input.substring(0, 5);
    }
    
    // Only allow one colon
    const colonCount = (input.match(/:/g) || []).length;
    if (colonCount > 1) {
      input = input.replace(/:/g, '').substring(0, 2) + ':' + input.replace(/:/g, '').substring(2, 4);
    }
    
    setTimeInputValue(input);
    
    // Try to parse if we have a complete time (HH:MM)
    if (input.length === 5 && input.includes(':')) {
      const parts = input.split(':');
      const hourStr = parts[0];
      const minuteStr = parts[1];
      
      if (hourStr && minuteStr && /^\d+$/.test(hourStr) && /^\d+$/.test(minuteStr)) {
        let hour = parseInt(hourStr, 10);
        const minute = parseInt(minuteStr, 10);
        
        // Validate and convert
        if (minute >= 0 && minute <= 59) {
          let period = time.period;
          
          // Handle 24-hour format
          if (hour >= 13 && hour <= 23) {
            period = 'PM';
            hour = hour - 12;
          } else if (hour === 0) {
            period = 'AM';
            hour = 12;
          } else if (hour === 12) {
            period = 'PM';
          } else if (hour >= 1 && hour <= 11) {
            // Keep current period for 1-11
            period = time.period;
          }
          
          if (hour >= 1 && hour <= 12) {
            handleTimeChange({ hour, minute, period });
          }
        }
      }
    }
  };

  const handleTimeDisplayBlur = (e) => {
    const input = timeInputValue;
    
    // Parse and validate on blur
    if (input.includes(':')) {
      const parts = input.split(':');
      const hourStr = parts[0];
      const minuteStr = parts[1];
      
      if (hourStr && minuteStr && /^\d+$/.test(hourStr) && /^\d+$/.test(minuteStr)) {
        let hour = parseInt(hourStr, 10);
        const minute = parseInt(minuteStr, 10);
        
        // Validate
        if (minute >= 0 && minute <= 59) {
          let period = time.period;
          
          // Handle 24-hour format
          if (hour >= 13 && hour <= 23) {
            period = 'PM';
            hour = hour - 12;
          } else if (hour === 0) {
            period = 'AM';
            hour = 12;
          } else if (hour === 12) {
            period = 'PM';
          }
          
          if (hour >= 1 && hour <= 12) {
            handleTimeChange({ hour, minute, period });
            setTimeInputValue(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
            return;
          }
        }
      }
    }
    
    // If invalid, reset to current time
    const currentHour = typeof time.hour === 'number' ? time.hour : 1;
    const currentMinute = typeof time.minute === 'number' ? time.minute : 0;
    setTimeInputValue(`${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`);
  };

  const togglePeriod = () => {
    handleTimeChange({ ...time, period: time.period === 'AM' ? 'PM' : 'AM' });
  };

  const handleInputClick = () => {
    if (!isOpen) {
      // Calculate if popup should show above
      setTimeout(() => {
        if (pickerRef.current && popupRef.current) {
          const rect = pickerRef.current.getBoundingClientRect();
          const popupHeight = popupRef.current.offsetHeight || 350;
          const spaceBelow = window.innerHeight - rect.bottom;
          const spaceAbove = rect.top;
          
          // Show above if not enough space below but enough space above
          if (spaceBelow < popupHeight + 20 && spaceAbove > popupHeight + 20) {
            setShowAbove(true);
          } else {
            setShowAbove(false);
          }
        }
      }, 0);
    }
    setIsOpen(!isOpen);
  };

  const displayValue = value ? formatDisplayTime(parseTimeValue(value) || time) : placeholder;

  return (
    <div className={`time-picker-wrapper ${className}`} ref={pickerRef}>
      <div 
        className={`time-picker-input ${error ? 'time-picker-input--error' : ''}`}
        onClick={handleInputClick}
      >
        <input
          type="text"
          id={id}
          name={name}
          value={displayValue}
          readOnly
          placeholder={placeholder}
          required={required}
          className="time-picker-input__field"
        />
        <svg className="time-picker-input__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          ref={popupRef}
          className={`time-picker-popup ${showAbove ? 'time-picker-popup--above' : ''}`}
        >
          <div className="time-picker-clock">
            <div className="time-picker-display">
              <input
                type="text"
                className="time-picker-display__time-input"
                value={timeInputValue || (time.hour && time.minute 
                  ? `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}` 
                  : '')}
                onChange={handleTimeDisplayChange}
                onBlur={handleTimeDisplayBlur}
                onFocus={(e) => {
                  // Select all text on focus for easy replacement
                  e.target.select();
                  if (!timeInputValue && time.hour && time.minute) {
                    setTimeInputValue(`${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`);
                  }
                }}
                placeholder="HH:MM"
                maxLength={5}
              />
              <div className="time-picker-display__period">{time.period}</div>
            </div>
            
            <div className="time-picker-controls">
              <div className="time-picker-control-group">
                <label className="time-picker-control-label">Hour</label>
                <div className="time-picker-control-buttons">
                  <button 
                    type="button"
                    className="time-picker-control-button"
                    onClick={incrementHour}
                  >
                    ▲
                  </button>
                  <input
                    type="text"
                    className="time-picker-control-value time-picker-control-value--editable"
                    value={time.hour === '' ? '' : (time.hour || 1)}
                    onChange={handleHourInputChange}
                    onBlur={handleHourBlur}
                    maxLength={2}
                  />
                  <button 
                    type="button"
                    className="time-picker-control-button"
                    onClick={decrementHour}
                  >
                    ▼
                  </button>
                </div>
              </div>
              
              <div className="time-picker-control-group">
                <label className="time-picker-control-label">Minute</label>
                <div className="time-picker-control-buttons">
                  <button 
                    type="button"
                    className="time-picker-control-button"
                    onClick={incrementMinute}
                  >
                    ▲
                  </button>
                  <input
                    type="text"
                    className="time-picker-control-value time-picker-control-value--editable"
                    value={time.minute === '' ? '' : String(time.minute || 0).padStart(2, '0')}
                    onChange={handleMinuteInputChange}
                    onBlur={handleMinuteBlur}
                    maxLength={2}
                  />
                  <button 
                    type="button"
                    className="time-picker-control-button"
                    onClick={decrementMinute}
                  >
                    ▼
                  </button>
                </div>
              </div>
              
              <div className="time-picker-control-group">
                <label className="time-picker-control-label">Period</label>
                <button 
                  type="button"
                  className="time-picker-period-button"
                  onClick={togglePeriod}
                >
                  {time.period}
                </button>
              </div>
            </div>
            
            <div className="time-picker-actions">
              <button
                type="button"
                className="time-picker-action-button time-picker-action-button--confirm"
                onClick={() => setIsOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <span className="time-picker-error">{error}</span>
      )}
    </div>
  );
};

export default TimePicker;

